import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Line } from 'react-chartjs-2'; 
import { 
    Chart as ChartJS, 
    CategoryScale, 
    LinearScale, 
    PointElement, 
    LineElement, 
    Title, 
    Tooltip, 
    Legend,
    Filler 
} from 'chart.js';

ChartJS.register(
    CategoryScale, 
    LinearScale, 
    PointElement, 
    LineElement, 
    Title, 
    Tooltip, 
    Legend,
    Filler 
); 

const indicatorContext = {
    vix: {
        name: "Indice VIX (Indice de la Peur)",
        description: "Le VIX mesure la volatilité implicite des options sur l'indice S&P 500. Une valeur élevée indique une peur ou une incertitude accrue sur le marché.",
        color: '#4e73df'
    },
    cpi: {
        name: "Indice des Prix à la Consommation (CPI)",
        description: "Le CPI est la mesure principale de l'inflation aux États-Unis, reflétant l'évolution des prix pour les consommateurs. Il est crucial pour les décisions de la Réserve Fédérale.",
        color: '#1cc88a'
    },
    tenY: {
        name: "Taux des Obligations du Trésor à 10 ans",
        description: "Ce taux est considéré comme le rendement 'sans risque' et sert de référence pour de nombreux autres taux d'intérêt (prêts immobiliers, entreprises). Il reflète les attentes d'inflation et de croissance économique.",
        color: '#e74a3b'
    },
};

const IndicatorDetail = () => {
    const { key } = useParams(); 
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // 🆕 SOLUTION RADICALE : Utiliser une ref pour détruire Chart.js proprement
    const chartInstance = useRef(null);
    const chartContainerRef = useRef(null);

    const context = indicatorContext[key] || {};

    useEffect(() => {
        const fetchHistory = async () => {
            setLoading(true);
            setError(null);
            
            const apiUrl = `/api/market/indicators/history/${key}`; 
            
            try {
                const response = await axios.get(apiUrl);
                setHistory(response.data.history || []);
                setLoading(false);
            } catch (err) {
                const errorMessage = err.response ? err.response.data.error : err.message;
                setError(`Erreur de chargement: ${errorMessage}. (URL: ${apiUrl})`);
                setLoading(false);
            }
        };

        if (key && context.name) {
            fetchHistory();
        } else if (key) {
            setError(`Indicateur '${key.toUpperCase()}' non défini.`);
            setLoading(false);
        }

    }, [key, context.name]);

    // 🆕 SOLUTION CRITIQUE : Nettoyage agressif de Chart.js
    useEffect(() => {
        return () => {
            // Détruire l'instance Chart.js si elle existe
            if (chartInstance.current) {
                try {
                    chartInstance.current.destroy();
                    chartInstance.current = null;
                } catch (e) {
                    console.warn('Erreur lors de la destruction du graphique:', e);
                }
            }
            
            // 🆕 NETTOYAGE MANUEL DU DOM - Solution radicale
            if (chartContainerRef.current) {
                const container = chartContainerRef.current;
                // Supprimer tous les canvas enfants
                const canvases = container.querySelectorAll('canvas');
                canvases.forEach(canvas => {
                    try {
                        if (canvas.parentNode === container) {
                            container.removeChild(canvas);
                        }
                    } catch (e) {
                        // Ignorer les erreurs removeChild
                    }
                });
            }
        };
    }, []);

    // 🆕 Gestionnaire de création d'instance Chart.js
    const handleChartRef = (chartRef) => {
        if (chartRef) {
            chartInstance.current = chartRef;
        }
    };

    const chartData = {
        labels: history.map(item => new Date(item.date).toLocaleDateString()),
        datasets: [{
            label: context.name || key?.toUpperCase(),
            data: history.map(item => item.value),
            fill: true,
            backgroundColor: context.color ? context.color + '33' : '#ccc33',
            borderColor: context.color || '#ccc',
            tension: 0.1,
            pointRadius: 0
        }]
    };
    
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            title: {
                display: true,
                text: `${context.name} - Historique Annuel`
            }
        },
        scales: {
            x: {
                title: { display: false },
                ticks: { maxTicksLimit: 10 }
            },
            y: {
                title: { display: true, text: 'Valeur' }
            }
        },
        // 🆕 OPTION IMPORTANTE : Désactiver certaines animations
        animation: {
            onComplete: () => {
                // Callback optionnel pour debug
            },
            onProgress: () => {
                // Callback optionnel pour debug
            }
        }
    };

    const latestValue = history.length > 0 ? history[history.length - 1]?.value.toFixed(2) : 'N/A';
    const latestDate = history.length > 0 ? new Date(history[history.length - 1]?.date).toLocaleDateString() : 'N/A';

    if (loading) {
        return <div className="container mt-5 p-5 text-center">Chargement de l'historique de {key?.toUpperCase()}...</div>;
    }

    if (error) {
        return <div className="container mt-5 p-5 text-danger bg-light border rounded">Erreur de Connexion ou de Données: {error}</div>;
    }

    if (!context.name) {
         return <div className="container mt-5 p-5 text-danger bg-light border rounded">Erreur 404: Indicateur {key?.toUpperCase()} non trouvé ou non supporté.</div>;
    }

    return (
        <div className="container py-5">
            <Link to="/dashboard" className="btn btn-secondary mb-4">
                &larr; Retour au Dashboard
            </Link>

            <div className="card shadow mb-4">
                <div className="card-header py-3 border-bottom-0">
                    <h1 className="m-0 fw-bold text-center" style={{color: context.color}}>
                        {context.name}
                    </h1>
                </div>
                <div className="card-body">
                    
                    <div className="mb-5 p-3 rounded" style={{ backgroundColor: context.color + '11' }}>
                        <h2 className="h4 text-gray-800">Contexte</h2>
                        <p>{context.description}</p>
                        <p className="text-muted">
                            Valeur la plus récente: <span className="fw-bold" style={{color: context.color}}>{latestValue}</span> au {latestDate}
                        </p>
                    </div>

                    {/* 🆕 CONTENEUR AVEC REF POUR NETTOYAGE MANUEL */}
                    <div 
                        ref={chartContainerRef}
                        className="chart-area" 
                        style={{ height: '400px', position: 'relative' }}
                    >
                        <h2 className="h4 text-gray-800 mb-3">Historique Détaillé (Annuel)</h2>
                        {history.length > 0 ? (
                            <Line 
                                ref={handleChartRef}
                                data={chartData} 
                                options={chartOptions}
                                // 🆕 KEY UNIQUE POUR FORCER LE NOUVEAU COMPOSANT
                                key={`chart-${key}-${history.length}`}
                            />
                        ) : (
                            <p>Aucune donnée historique disponible pour l'affichage.</p>
                        )}
                    </div>

                    <div className="mt-5">
                        <h3 className="h5 mb-3">Données récentes</h3>
                        <table className="table table-striped table-bordered">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Valeur</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.slice(-10).reverse().map((item, index) => (
                                    <tr key={index}>
                                        <td>{new Date(item.date).toLocaleDateString()}</td>
                                        <td>{item.value !== undefined ? item.value.toFixed(2) : 'N/A'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndicatorDetail;